import { CheckCircle, Keyboard, Send, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import type { WriteAgentState } from "./types";

interface WriteAgentSheetProps {
  isOpen: boolean;
  state: WriteAgentState;
  inputText: string;
  response: string | null;
  error: string | null;
  elapsedSeconds?: number;
  onInputChange: (text: string) => void;
  onSubmit: () => void;
  onNewRequest: () => void;
  onClose: () => void;
}

export function WriteAgentSheet({
  isOpen,
  state,
  inputText,
  response,
  error,
  elapsedSeconds = 0,
  onInputChange,
  onSubmit,
  onNewRequest,
  onClose,
}: WriteAgentSheetProps) {
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="flex flex-col h-64">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {state === "open" && (
              <>
                <Keyboard className="h-4 w-4 text-primary" />
                Write your request
              </>
            )}
            {state === "processing" &&
              (elapsedSeconds > 0
                ? `Processing... (${elapsedSeconds}s)`
                : "Processing...")}
            {state === "response" && (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                Done
              </>
            )}
            {state === "error" && "Something went wrong"}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 py-2 overflow-auto">
          {state === "open" && (
            <Textarea
              autoFocus
              rows={3}
              placeholder="Type your request..."
              value={inputText}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              className="resize-none"
            />
          )}
          {state === "processing" && (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
          {state === "response" && response && (
            <p className="text-sm">{response}</p>
          )}
          {state === "error" && error && (
            <p className="text-destructive text-sm">{error}</p>
          )}
        </div>

        <div className="flex gap-3 justify-center pb-4">
          {state === "open" && (
            <button
              onClick={onSubmit}
              disabled={!inputText.trim()}
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-2 text-sm text-primary-foreground disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              Send
            </button>
          )}
          {(state === "response" || state === "error") && (
            <>
              <button
                onClick={onNewRequest}
                className="flex items-center gap-2 rounded-md border px-4 py-2 text-sm"
              >
                <Keyboard className="h-4 w-4" />
                New request
              </button>
              <button
                onClick={onClose}
                className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
              >
                <X className="h-4 w-4" />
                Close
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
